import { useState, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { PhoneCall, X, Loader2, CheckCircle2 } from 'lucide-react';
import { tokens } from '../theme';

/**
 * VoiceCallModal — Fase "Llamada IA" (Sergio, 08-sep). El visitante deja nombre + país + número y la IA
 * de LeadCEM le MARCA. Postea al webhook del tenant LeadCEM (mismo-origen) bajo la campaña "Llamada IA
 * landing Page"; el intake corre su proceso natural (dedup → ruleta → schedule_ivr_call force_ivr) y la IA
 * marca desde el número del tenant (+525571001192, company_voice_config). No requiere backend nuevo.
 */
const LEADCEM_COMPANY = '75cd988e-6e43-4222-b2b9-3ea11cd46381';
const LEADCEM_CAMPAIGN = 'c4fa1bef-9f91-4bd3-86bb-548765b332c9';  // campana 'Llamada IA landing Page' -> receta Voz IA acotada (la IA marca)
const COUNTRIES = [
  { c: 'MX', name: 'México', d: '+52', flag: '🇲🇽' },
  { c: 'US', name: 'Estados Unidos', d: '+1', flag: '🇺🇸' },
  { c: 'CO', name: 'Colombia', d: '+57', flag: '🇨🇴' },
  { c: 'AR', name: 'Argentina', d: '+54', flag: '🇦🇷' },
  { c: 'ES', name: 'España', d: '+34', flag: '🇪🇸' },
  { c: 'PE', name: 'Perú', d: '+51', flag: '🇵🇪' },
  { c: 'CL', name: 'Chile', d: '+56', flag: '🇨🇱' },
  { c: 'EC', name: 'Ecuador', d: '+593', flag: '🇪🇨' },
  { c: 'GT', name: 'Guatemala', d: '+502', flag: '🇬🇹' },
  { c: 'CR', name: 'Costa Rica', d: '+506', flag: '🇨🇷' },
  { c: 'PA', name: 'Panamá', d: '+507', flag: '🇵🇦' },
  { c: 'DO', name: 'Rep. Dominicana', d: '+1', flag: '🇩🇴' },
];

export default function VoiceCallModal({ open, onClose }) {
  const { theme } = useTheme();
  const t = tokens(theme);
  const [name, setName] = useState('');
  const [dial, setDial] = useState('+52');
  const [phone, setPhone] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | done | error

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const digits = phone.replace(/\D/g, '');
  const valid = name.trim().length >= 2 && digits.length >= 8;
  const fullPhone = dial + digits;

  const submit = async () => {
    if (!valid || status === 'sending') return;
    setStatus('sending');
    try {
      const res = await fetch(`/api/webhook/lead/${LEADCEM_COMPANY}?campaign_id=${LEADCEM_CAMPAIGN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: name.trim(),
          phone: fullPhone,
          source: 'landing_llamada_ia',
          campaign_name: 'Llamada IA landing Page',
          notes: 'Solicitó que la IA de LeadCEM le llame por voz (popup landing).',
        }),
      });
      if (!res.ok) throw new Error('bad');
      setStatus('done');
    } catch (e) {
      setStatus('error');
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Que la IA te llame" data-testid="voice-call-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative w-full max-w-md rounded-3xl p-6 sm:p-8 shadow-2xl ${t.dark ? 'bg-[#0b1730] border border-white/10' : 'bg-white border border-slate-200'}`}>
        <button onClick={onClose} className={`absolute top-4 right-4 ${t.textFaint} hover:opacity-70`} aria-label="Cerrar"><X className="w-5 h-5" /></button>

        {status !== 'done' ? (
          <>
            <div className="w-12 h-12 rounded-2xl bg-lime-400 text-slate-950 flex items-center justify-center mb-4"><PhoneCall className="w-6 h-6" /></div>
            <h3 className={`text-xl sm:text-2xl font-semibold ${t.text}`}>Que la IA te llame</h3>
            <p className={`text-sm ${t.textSub} mt-2 mb-5`}>Déjanos tu número y la IA de LeadCEM te marca <b className={t.text}>en menos de un minuto</b>. Escúchala tú mismo.</p>

            <label htmlFor="vc-name" className={`block text-xs font-semibold uppercase tracking-wide ${t.textFaint} mb-1.5`}>Tu nombre</label>
            <input id="vc-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre"
              className={`w-full rounded-xl px-4 h-11 mb-4 outline-none transition-colors ${t.dark ? 'bg-white/5 border border-white/15 text-white placeholder-white/40 focus:border-lime-400' : 'bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-lime-500'}`} data-testid="vc-name" />

            <label htmlFor="vc-phone" className={`block text-xs font-semibold uppercase tracking-wide ${t.textFaint} mb-1.5`}>¿A qué número te llama?</label>
            <div className="flex gap-2 mb-5">
              <select value={dial} onChange={(e) => setDial(e.target.value)} aria-label="País"
                className={`rounded-xl px-2 h-11 outline-none transition-colors ${t.dark ? 'bg-white/5 border border-white/15 text-white focus:border-lime-400' : 'bg-slate-50 border border-slate-300 text-slate-900 focus:border-lime-500'}`} data-testid="vc-country">
                {COUNTRIES.map((c) => <option key={c.c + c.d} value={c.d}>{c.flag} {c.d}</option>)}
              </select>
              <input id="vc-phone" value={phone} onChange={(e) => setPhone(e.target.value)} inputMode="tel" placeholder="10 dígitos"
                className={`flex-1 rounded-xl px-4 h-11 outline-none transition-colors ${t.dark ? 'bg-white/5 border border-white/15 text-white placeholder-white/40 focus:border-lime-400' : 'bg-slate-50 border border-slate-300 text-slate-900 placeholder-slate-400 focus:border-lime-500'}`} data-testid="vc-phone" />
            </div>

            <Button onClick={submit} disabled={!valid || status === 'sending'}
              className="lc-shine w-full bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full h-12 font-semibold disabled:opacity-50 disabled:cursor-not-allowed" data-testid="vc-submit">
              {status === 'sending'
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Pidiendo tu llamada…</>
                : <><PhoneCall className="w-4 h-4 mr-2" /> Que la IA me llame ahora</>}
            </Button>
            {status === 'error' && <p className="text-xs text-red-400 mt-3 text-center">No se pudo registrar. Intenta de nuevo o escríbenos por WhatsApp.</p>}
            <p className={`text-[11px] ${t.textFaint} mt-3 text-center`}>Te llama la misma IA que atendería a tus clientes. Sin costo para ti.</p>
          </>
        ) : (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-lime-400/15 text-lime-400 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="w-8 h-8" /></div>
            <h3 className={`text-xl font-semibold ${t.text}`}>¡Listo! Contesta tu teléfono 📞</h3>
            <p className={`text-sm ${t.textSub} mt-2`}>La IA de LeadCEM te va a llamar en menos de un minuto al <b className={t.text}>{fullPhone}</b>. Escúchala como la escucharían tus clientes.</p>
            <Button onClick={onClose} className={`mt-6 rounded-full h-11 px-6 ${t.iconChipNavy} hover:opacity-90`}>Cerrar</Button>
          </div>
        )}
      </div>
    </div>
  );
}
